import React, { useEffect, useState, useCallback, useRef } from "react";
import Input from "../../components/controls/input/customInput/CustomInput";
import invoiceRegistryService from "../../store/services/invoiceRegistry.service";
import projectService from "../../store/services/projects.service";
import Searchable from "../../components/controls/select/CustomSelect";
import _ from "lodash";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import { calculatePageSize, changeRequestTypes, getLabelByType } from "../../helpers/utils";
import Table from "../../components/controls/tables/Table";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import { CalendarIcon } from "../../modules/default-svg";
import {
  getAccountCurrency,
  displayDecimalFormatted,
} from "../../utils/systemSettings";
import {
  createInvoiceRegistryRequest,
  deleteInvoiceRegistryRequest,
  getInvoiceRegistryRequest,
  updateInvoiceRegistryRequest,
} from "../../store/actions/invoiceRegistry.actions";
import Skeleton from "react-loading-skeleton";
import { debounce } from "lodash";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import ModalCheckbox from "../../components/controls/modalcheckbox/ModalCheckbox";
import { Receipt, Plus, Trash2 } from "lucide-react";

// Overpayment cap: paid amount can't exceed the invoice Total. Mirrors
// EnforcePaymentCap in InvoiceEntryService.cs - keep both in sync.
const ENFORCE_PAYMENT_CAP = true;

const InvoiceRegistry = (props) => {
  const { projectID, allInvoiceRegistries, loading, pagination, pageLoading } =
    props;

  const dispatch = useDispatch();
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [data, setData] = useState([]);
  const [perPageItems] = useState(calculatePageSize());
  const [pageNum, setPageNum] = useState(1);
  const [selectedRow, setSelectedRow] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [isCreateNewRow, setIsCreateNewRow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeletedId] = useState(null);
  const [invoiceRegistry, setInvoiceRegistry] = useState({
    id: "",
    date: null,
    dueDate: null,
    supplierInvId: "",
    internalInvId: "",
    valueBeforeTax: 0,
    tax: 0,
    account: "",
    comments: "",
    purchaseOrderId: "",
    isInLedger: 0,
    // An invoice needs no payments at all - with none, its whole Total is accrued.
    payments: [],
  });
  const [attachments, setAttachments] = useState([]);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [paymentDateErrors, setPaymentDateErrors] = useState({});
  const [cashflowLineItem, setCashflowLineItem] = useState({
    lineId: "",
    lineCategory: null,
    lineDescription: "",
    label: "",
  });
  const [cashflowLineOptions, setCashflowLineOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({
    po: "",
    p_code: "",
    date: "",
  });

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [purchaseOrder, setPurchaseOrder] = useState({
    id: "",
    name: "",
    vendor: "",
    value: 0,
    changeRequest: null,
  });
  const [tableSearch, setTableSearch] = useState(null);

  const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch = false) => {
    const payload = { page: pageNumber ?? pageNum, size: pageSize ?? perPageItems, search: forceClearSearch ? null : (searchTerm ?? tableSearch) };
    payload.parent = "Project";

    if (projectID) {
      payload.id = projectID;
      payload.parent = "Project";
    }
    else {
      setData([]);
      return;
    }

    dispatch(getInvoiceRegistryRequest(payload));
  };
  const handleAddRow = useCallback(() => {
    setIsCreateNewRow(true);
    fetchData(pagination.TotalPages ?? 1, perPageItems > 10 ? perPageItems - 5 : perPageItems - 3);
  }, []);

  const handleDelete = (selectedRow) => {
    const id = selectedRow.id;
    dispatch(
      deleteInvoiceRegistryRequest({
        id: id,
        parentID: projectID,
        parent: "Project",
      })
    );
    setDeletedId(id);
  };

  const initDataState = () => {
    setFormErrors({});
    setPaymentErrors({});
    setPaymentDateErrors({});
    setInvoiceRegistry({
      id: "",
      date: null,
      dueDate: null,
      supplierInvId: "",
      internalInvId: "",
      valueBeforeTax: 0,
      tax: 0,
      account: "",
      comments: "",
      purchaseOrderId: "",
      isInLedger: 0,
      payments: [],
    });
    setAttachments([]);
    setCashflowLineItem({
      lineId: "",
      lineCategory: null,
      lineDescription: "",
      label: "",
    });
    setPurchaseOrder({
      id: "",
      name: "",
      vendor: "",
      value: 0,
      changeRequest: null
    });
    setPurchaseOrders([]);
    setCashflowLineOptions([]);
  };

  const handleSave = () => {
    const errors = {};
    if (!invoiceRegistry.purchaseOrderId) errors["po"] = `Required`;
    if (!cashflowLineItem.lineId) errors["p_code"] = `Required`;
    if (!invoiceRegistry.date) errors["date"] = `Required`;

    // A payment row only ever represents an actual payment, so its date is mandatory.
    const dateErrors = {};
    invoiceRegistry.payments.forEach((p, index) => {
      if (!p.paidDate) dateErrors[index] = "Required";
    });
    setPaymentDateErrors(dateErrors);

    setFormErrors(errors);
    if (Object.keys(errors).length > 0 || Object.keys(dateErrors).length > 0) {
      return;
    }
    if (!isEdit) invoiceRegistry.projectId = projectID;

    invoiceRegistry.cashflowLineId = cashflowLineItem.lineId;

    if (Object.keys(selectedRow).length > 0) {
      dispatch(
        updateInvoiceRegistryRequest({
          id: selectedRow.id,
          parentID: projectID,
          parent: "Project",
          payload: invoiceRegistry,
          perPageItems,
        })
      );
      return;
    } else {
      dispatch(
        createInvoiceRegistryRequest({
          parentID: projectID,
          parent: "Project",
          payload: invoiceRegistry,
          perPageItems,
        })
      );
    }
  };

  const fetchInvoiceRegistryData = async () => {
    setIsLoading(true);
    invoiceRegistryService
      .fetchSingleInvoiceRegistryRequest(selectedRow.id)
      .then((data) => {
        setInvoiceRegistry({
          id: data.id,
          date: data.date,
          dueDate: data.dueDate,
          supplierInvId: data.supplierInvId,
          internalInvId: data.internalInvId,
          valueBeforeTax: data.valueBeforeTax,
          tax: data.tax,
          account: data.account,
          comments: data.comments,
          purchaseOrderId: data.purchaseOrder?.id,
          isInLedger: data.isInLedger,
          payments: (data.payments || []).map((p) => ({
            id: p.id,
            amount: p.amount,
            paidDate: p.paidDate,
          })),
        });
        setPurchaseOrders([data.purchaseOrder]);
        setAttachments(data.attachments || []);
        setPurchaseOrder({
          id: data.purchaseOrder?.name,
          name: data.purchaseOrder?.name,
          vendor: data.purchaseOrder?.vendor,
          value: data.purchaseOrder?.value,
          changeRequest: data.purchaseOrder?.changeRequest
        });
        setCashflowLineItem({
          lineId: data.cashflowLine?.id,
          lineCategory: data.cashflowLine?.category,
          lineDescription: data.cashflowLine?.description,
          label: `${getLabelByType("cashflowLineCategory", data.cashflowLine?.category)} - ${data.cashflowLine?.description}`,
        });
        if (data.cashflowLine?.id)
          handleCashflowLineChange(
            {
              value: data.cashflowLine?.id,
              label: `${getLabelByType("cashflowLineCategory", data.cashflowLine?.category)} - ${data.cashflowLine?.description}`,
            },
            true
          );
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title:
              "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const handleCancel = () => {
    setIsEdit(false);
    initDataState();
  };

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0) {
      initDataState();
      isEdit && fetchInvoiceRegistryData();
    }
  }, [selectedRow, isEdit]);

  useEffect(() => {
    fetchData();
  }, [pageNum, perPageItems]);

  const handleCancelAddRow = () => {
    setIsCreateNewRow(false);
    fetchData();
  };

  const handleSearch = (filteredData) => {
    setData(filteredData);
  };

  useEffect(() => {
    if (allInvoiceRegistries && allInvoiceRegistries.length > 0) {
      if (isCreateNewRow) {
        setData(allInvoiceRegistries.sort((a, b) => b.isFlagged - a.isFlagged));
        setIsCreateNewRow(false);
      } else {
        if (pageNum === 1) {
          setData(
            allInvoiceRegistries
              .filter((item) => item.id !== deleteId)
              .sort((a, b) => {
                return b.isFlagged - a.isFlagged;
              })
          );
        }
        else {
          setData((prevData) => {
            const newMap = new Map(
              allInvoiceRegistries.map((item) => [item.id, item])
            );

            const updatedPrevData = prevData.map((item) =>
              newMap.has(item.id) ? newMap.get(item.id) : item
            );

            const prevDataIds = new Set(prevData.map((item) => item.id));
            const newItems = allInvoiceRegistries.filter(
              (shift) => !prevDataIds.has(shift.id)
            );

            return [...updatedPrevData, ...newItems]
              .filter((item) => item.id !== deleteId)
              .sort((a, b) => b.isFlagged - a.isFlagged);
          });
        }

      }
    } else if (allInvoiceRegistries && allInvoiceRegistries.length === 0) {
      setData([]);
    }
  }, [allInvoiceRegistries]);

  const handleCreate = (val) => {
    initDataState();
    setIsCreate(val);
  };

  const handleInputChange = (name, value) => {
    setInvoiceRegistry((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddPayment = () => {
    setInvoiceRegistry((prev) => ({
      ...prev,
      payments: [...prev.payments, { id: null, amount: 0, paidDate: null }],
    }));
    setPaymentErrors({});
    setPaymentDateErrors({});
  };
  const handleRemovePayment = (index) => {
    setInvoiceRegistry((prev) => ({
      ...prev,
      payments: prev.payments.filter((_, i) => i !== index),
    }));
    setPaymentErrors({});
    setPaymentDateErrors({});
  };
  // Mirrors the backend's cap (ENFORCE_PAYMENT_CAP / EnforcePaymentCap): the sum of
  // all (actual, dated) payments can never exceed the invoice Total. The entered
  // value is kept as-is (clearing it here caused the controlled input to briefly
  // reset, which fired another change event and wiped the error before the user
  // could see it) - instead an inline error is shown under the offending row and
  // the modal's Save button is disabled until it's fixed (see isModalSaveBtnDisabled
  // below).
  const handlePaymentChange = (index, field, value) => {
    setInvoiceRegistry((prev) => {
      const nextPayments = prev.payments.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      );

      if (ENFORCE_PAYMENT_CAP && field === "amount") {
        const total = (parseFloat(prev.valueBeforeTax) || 0) + (parseFloat(prev.tax) || 0);
        const paymentsSum = nextPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

        if (paymentsSum > total) {
          const otherSum = nextPayments
            .filter((p, i) => i !== index)
            .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
          const maxAllowed = Math.max(total - otherSum, 0);

          setPaymentErrors((prevErrors) => ({
            ...prevErrors,
            [index]: `You can't go over ${getAccountCurrency(settings)} ${displayDecimalFormatted(settings, maxAllowed)} for this payment`,
          }));

          return { ...prev, payments: nextPayments };
        }
      }

      setPaymentErrors((prevErrors) => {
        if (!prevErrors[index]) return prevErrors;
        const next = { ...prevErrors };
        delete next[index];
        return next;
      });

      if (field === "paidDate" && value) {
        setPaymentDateErrors((prevErrors) => {
          if (!prevErrors[index]) return prevErrors;
          const next = { ...prevErrors };
          delete next[index];
          return next;
        });
      }

      return { ...prev, payments: nextPayments };
    });
  };

  // Mirrors the backend's Total/Status calculation so the modal reflects the
  // effect of in-progress edits before Save - see InvoiceStatusCalculator.cs.
  // Every payment row is an actual, dated payment, so their sum is both "paid so
  // far" and the amount that counts against the overpayment cap.
  const total = (parseFloat(invoiceRegistry.valueBeforeTax) || 0) + (parseFloat(invoiceRegistry.tax) || 0);
  const paidAmount = (invoiceRegistry.payments || [])
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const invoiceStatus =
    paidAmount <= 0
      ? 0 // Unpaid
      : paidAmount === total
      ? 2 // Paid
      : paidAmount > total
      ? 3 // Overpaid
      : 1; // PartiallyPaid
  // No room left to allocate a new payment once existing ones already reach the Total.
  const isAddPaymentDisabled = ENFORCE_PAYMENT_CAP && paidAmount >= total;
  const hasPaymentErrors = Object.keys(paymentErrors).length > 0;

  const handleCashflowLineItemChange = (name, value) => {
    setCashflowLineItem((prev) => ({ ...prev, [name]: value }));
  };
  // const handlePurchaseOrderChange = (name, value) => {
  //   setPurchaseOrder((prev) => ({ ...prev, [name]: value }));
  // };
  const handleCashflowLineChange = (line, isInit = false) => {
    handleCashflowLineItemChange("lineId", line?.value);
    handleCashflowLineItemChange("label", line?.label);

    if (!line?.value) {
      return;
    }
  };
  const cashflowLineSearchdebounced = useRef(
    debounce((value, params) => searchCashflowLineHandler(value, params), 500)
  );
  const handleCashflowLineSearch = useCallback((value, params) => {
    cashflowLineSearchdebounced.current(value, params);
  }, []);

  const poSearchdebounced = useRef(
    debounce((value) => searchPurchaseOrderHandler(value), 500)
  );
  const handlePoSearch = useCallback((value) => {
    poSearchdebounced.current(value);
  }, []);

  const searchCashflowLineHandler = (value, params) => {
    setCashflowLineOptions([]);
    if (!value) value = "";
    const searchParams = { description: value, changeRequestId: params.changeRequestId };
    projectService
      .getProjectCashflowLookup(projectID, searchParams)
      .then((data) => {
        setCashflowLineOptions(data.lines);
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title:
              "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => { });
  };
  const searchPurchaseOrderHandler = (value) => {
    if (!value) value = "";
    projectService
      .getProjectPurchaseOrders(projectID, value)
      .then((data) => {
        setPurchaseOrders(data);
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title:
              "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => { });
  };

  const handlePOChange = (poItem) => {
    let isEmptyPO = !poItem;
    let selected;
    if (!isEmptyPO) {
      selected = purchaseOrders.find((item) => item.id === poItem.value);
      if (selected) {
        handleInputChange("purchaseOrderId", selected.id);
        setPurchaseOrder(selected);
      } else {
        isEmptyPO = true;
      }
    }

    if (isEmptyPO) {
      setPurchaseOrder({
        id: "",
        name: "",
        vendor: "",
        value: 0,
        changeRequest: null
      });
      handleInputChange("purchaseOrderId", "");
    }
    handleCashflowLineChange(null);
    handleCashflowLineSearch("", {
      changeRequestId: selected ? selected?.changeRequest?.id : null,
    });
  };

  const formBody = () => {
    return (
      <>
        <div className="d-flex flex-col">
          <div className="row align-items-end w-100 mb-4 gx-3">
            <div className="col-4">
              <ModalCheckbox
                label={invoiceRegistry.isInLedger ? "Included In Ledger" : "Include In Ledger"}
                checked={invoiceRegistry.isInLedger}
                setChecked={(value) => {
                  handleInputChange("isInLedger", value);
                }}
                loading={isLoading}
              />
            </div>
          </div>
          <div className="row w-100 mb-3 gx-3">
            <div className="col-4">
              <Searchable
                label="PO #"
                isRequired
                SelectText="PO"
                selectWd="me-3"
                defaultVal={
                  purchaseOrder?.id
                    ? { value: purchaseOrder.id, label: purchaseOrder.name }
                    : null
                }
                searchText={(value) => {
                  handlePoSearch(value);
                }}
                options={purchaseOrders.map((option) => ({
                  label: option.name,
                  value: option.id,
                }))}
                onChange={(value) => {
                  handlePOChange(value);
                }}
                loading={isLoading}
                errorMessage={formErrors.po}
              />
            </div>
            <div className="row col-8 flex gx-3">
              {purchaseOrder?.id && purchaseOrder?.changeRequest && (
                <div className="flex-1 flex flex-col">
                  <label className="Input_label">Change Request</label>
                  {isLoading ? (
                    <Skeleton height={40} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                  ) : (
                    <p
                      className="text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md"
                      style={{ border: "1px solid #342C82" }}
                    >
                      {purchaseOrder?.changeRequest?.name || ""}
                    </p>
                  )}
                </div>
              )}
              {purchaseOrder?.id && (
                <div className="flex-1 flex flex-col">
                  <label className="Input_label">Supplier Name</label>
                  {isLoading ? (
                    <Skeleton height={40} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                  ) : (
                    <p
                      className="text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md"
                      style={{ border: "1px solid #342C82" }}
                    >
                      {purchaseOrder?.vendor || ""}
                    </p>
                  )}
                </div>
              )}
              {purchaseOrder?.id && (
                <div className="flex-1 flex flex-col">
                  <label className="Input_label">Value</label>
                  {isLoading ? (
                    <Skeleton height={40} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                  ) : (
                    <p
                      className="text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md"
                      style={{ border: "1px solid #342C82" }}
                    >
                      {`${getAccountCurrency(settings)} ${displayDecimalFormatted(
                        settings,
                        purchaseOrder?.value
                      )}`}
                    </p>
                  )}
                </div>
              )}
            </div>

          </div>
          <div className="row align-items-end w-100 mb-5 gx-3">
            <div className="col-4">
              <Searchable
                label="Payment Code"
                SelectText="Code"
                selectWd="me-3"
                isRequired
                defaultVal={
                  cashflowLineItem.lineId
                    ? {
                      value: cashflowLineItem.lineId,
                      label: cashflowLineItem.label,
                    }
                    : null
                }
                searchText={(value) => {
                  handleCashflowLineSearch(value, {
                    changeRequestId: purchaseOrder?.changeRequest ? purchaseOrder?.changeRequest.id : null,
                  });
                }}
                options={cashflowLineOptions.map((option) => ({
                  label: `${getLabelByType("cashflowLineCategory", option.category)} - ${option.description}`,
                  value: option.id,
                }))}
                onChange={(line) => {
                  handleCashflowLineChange(line);
                }}
                loading={isLoading}
                errorMessage={formErrors.p_code}
              />
            </div>
            <div className="col-4">
              <Input
                icon={<CalendarIcon />}
                value={invoiceRegistry?.date}
                type="date"
                isRequired
                handleChange={(value) => {
                  handleInputChange("date", value);
                }}
                label="Invoice Date"
                placeholder="Enter Invoice Date"
                loading={isLoading}
                errorMessage={formErrors.date}
              />
            </div>
          </div>
          <div className="row w-100 mb-3 gx-3">
            <div className="col-4">
              <Input
                value={invoiceRegistry.account ?? ""}
                type="text"
                handleChange={(value) => {
                  handleInputChange("account", value);
                }}
                label="Account"
                placeholder="Enter Account"
                loading={isLoading}
              />
            </div>
            <div className="col-4">
              <Input
                inputwd=""
                icon={false}
                placeholder="Enter Supplier Invoice"
                value={invoiceRegistry.supplierInvId}
                type="text"
                handleChange={(value) => {
                  handleInputChange("supplierInvId", value);
                }}
                label="Supplier Inv #"
                loading={isLoading}
              />
            </div>
            <div className="col-4">
              <Input
                value={invoiceRegistry.internalInvId ?? ""}
                type="text"
                handleChange={(value) => {
                  handleInputChange("internalInvId", value);
                }}
                label="Internal In #"
                placeholder="Enter Internal Invoice"
                loading={isLoading}
              />
            </div>
          </div>
          <div className="row align-items-end w-100 mb-3 gx-3">
            <div className="col-4">
              <Input
                inputwd=""
                icon={false}
                placeholder="Enter Value Before Tax"
                value={invoiceRegistry.valueBeforeTax}
                type="number"
                handleChange={(value) => {
                  handleInputChange("valueBeforeTax", value);
                }}
                label="Value Before Tax"
                currency={true}
                loading={isLoading}
              />
            </div>
            <div className="col-4">
              <Input
                inputwd=""
                icon={false}
                placeholder="Enter Tax"
                value={invoiceRegistry.tax}
                type="number"
                handleChange={(value) => {
                  handleInputChange("tax", value);
                }}
                label="Tax"
                currency={true}
                loading={isLoading}
              />
            </div>
            <div className="col-4 flex flex-col">
              <label className="Input_label">Total</label>
              {isLoading ? (
                <Skeleton height={40} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
              ) : (
                <p
                  className="text-[#1F1F1F] bg-[#EDECF8] text-center py-[9px] rounded-md mb-0"
                  style={{ border: "1px solid #342C82" }}
                >
                  {`${getAccountCurrency(settings)} ${displayDecimalFormatted(settings, total)}`}
                </p>
              )}
            </div>
          </div>
          <div
            className="bg-white border border-gray-300 rounded-lg p-3 mb-4"
            style={{ fontFamily: "Figtree" }}
          >
            <div className="flex align-items-center justify-content-between mb-4 pb-3 border-b border-gray-200">
              <div className="flex align-items-center gap-2">
                <Receipt className="w-4 h-4" style={{ color: "#342c82" }} />
                <span className="text-sm font-semibold text-gray-800">Payments</span>
                <button
                  type="button"
                  onClick={handleAddPayment}
                  disabled={isLoading || isAddPaymentDisabled}
                  title={isAddPaymentDisabled ? `Payments already reach the Total of ${getAccountCurrency(settings)} ${displayDecimalFormatted(settings, total)}` : "Add payment"}
                >
                  <Plus
                    size={20}
                    className={`p-[2px] bg-none border-none text-[#342c82] rounded duration-100 ease-in-out ${
                      isAddPaymentDisabled
                        ? "opacity-30 cursor-not-allowed"
                        : "cursor-pointer hover:bg-[#342c82] hover:text-white"
                    }`}
                  />
                </button>
              </div>
              <div className="flex align-items-center gap-4">
                <div className="flex align-items-center gap-2">
                  <span className="text-xs text-gray-600 font-medium">Paid of Total</span>
                  {isLoading ? (
                    <Skeleton width={100} height={25} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                  ) : (
                    <p
                      style={{ border: "1px solid #342C82" }}
                      className="d-flex align-items-center justify-star px-[8px] text-[#1F1F1F] bg-[#EDECF8] text-xs font-bold text-center rounded-md h-[25px] mb-0"
                    >
                      {getAccountCurrency(settings)} {displayDecimalFormatted(settings, paidAmount)} / {getAccountCurrency(settings)} {displayDecimalFormatted(settings, total)}
                    </p>
                  )}
                </div>
                {paidAmount > 0 && (
                  <div className="flex align-items-center gap-2">
                    <span className="text-xs text-gray-600 font-medium">Balance</span>
                    {isLoading ? (
                      <Skeleton width={100} height={25} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                    ) : (
                      <p
                        style={{ border: "1px solid #342C82" }}
                        className="d-flex align-items-center justify-star px-[8px] text-[#1F1F1F] bg-[#EDECF8] text-xs font-bold text-center rounded-md h-[25px] mb-0"
                      >
                        {getAccountCurrency(settings)} {displayDecimalFormatted(settings, Math.max(total - paidAmount, 0))}
                      </p>
                    )}
                  </div>
                )}
                <div className="flex align-items-center gap-2">
                  <span className="text-xs text-gray-600 font-medium"></span>
                  {isLoading ? (
                    <Skeleton width={90} height={25} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
                  ) : (
                    <p className={`pill mb-0 ${invoiceStatus === 2 ? "Completed" : (invoiceStatus === 3 ? "AtRisk" : "secondary")}`} style={{ height: 25 }}>
                      {getLabelByType("invoiceStatus", invoiceStatus)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex flex-column gap-2">
              {invoiceRegistry.payments.length === 0 && (
                <p className="text-xs text-gray-600 mb-0 text-center">
                  No payments recorded yet.
                </p>
              )}
              {invoiceRegistry.payments.map((payment, index) => (
                <div
                  key={payment.id ?? `new-${index}`}
                  className="d-flex align-items-end gap-2 bg-white border border-gray-200 rounded-xl p-2 transition duration-150 ease-in-out hover:border-gray-300 hover:shadow-md"
                >
                  <div className="flex-1">
                    <Input
                      inputwd=""
                      icon={false}
                      placeholder="Enter Amount"
                      value={payment.amount}
                      type="number"
                      handleChange={(value) => handlePaymentChange(index, "amount", value)}
                      label={index === 0 ? "Amount" : undefined}
                      currency={true}
                      loading={isLoading}
                      errorMessage={paymentErrors[index]}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      icon={<CalendarIcon />}
                      value={payment.paidDate}
                      minDate={invoiceRegistry?.date}
                      type="date"
                      isRequired
                      handleChange={(value) => handlePaymentChange(index, "paidDate", value)}
                      label={index === 0 ? "Paid Date" : undefined}
                      placeholder="Enter Paid Date"
                      loading={isLoading}
                      errorMessage={paymentDateErrors[index]}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemovePayment(index)}
                    disabled={isLoading}
                    title="Remove payment"
                    className="d-flex align-items-center justify-content-center flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{
                      width: 36,
                      height: 36,
                      color: "#dc2626",
                      borderRadius: 8,
                      border: "none",
                      background: "transparent",
                    }}
                    onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#fee2e2"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="row w-100 gx-3 mb-3">
            <RichTextEditor
              label="Comments"
              content={invoiceRegistry.comments}
              onChange={(value) => {
                handleInputChange("comments", value);
              }}
              loading={isLoading}
            />
          </div>
          <EntityFileUpload
            entityId={selectedRow.id}
            entityType="Invoice"
            attachments={attachments}
            onAttachmentsChange={() => {
              if (selectedRow.id) {
                invoiceRegistryService
                  .fetchSingleInvoiceRegistryRequest(selectedRow.id)
                  .then((invoiceRegistryData) => {
                    setAttachments(invoiceRegistryData.attachments);
                  })
                  .catch((error) => {
                    console.error(
                      "Error fetching invoice registry data:",
                      error
                    );
                  });
              }
            }}
            disabled={isLoading || !selectedRow.id}
          />
        </div>
      </>
    );
  };

  const handleSearchTrigger = (searchTerm) => {
    if ((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;

    let pageNumber = pageNum;
    if (!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)) {
      setData([]); //clear existing data
      pageNumber = 1; //reset to first page
    }

    setTableSearch(searchTerm);
    fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);
  };

  return (
    <ColumnConfigProvider tableName="InvoiceRegistry">
      <div className="listingScreen position-relative">
        <Table
          type="Invoice"
          rows={data || []}
          body={formBody()}
          searchHandler={handleSearch}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          setPageNum={setPageNum}
          perPageItems={perPageItems}
          setIsCreate={(val) => {
            handleCreate(val);
          }}
          loading={pageLoading}
          modalLoading={loading}
          isCreate={isCreate}
          pagination={pagination}
          isModalSaveBtnDisabled={hasPaymentErrors}
          handleSave={() => handleSave()}
          handleDelete={(selectedRow) => handleDelete(selectedRow)}
          handleCancel={() => handleCancel()}
          setIsEdit={setIsEdit}
          setSelectedRow={setSelectedRow}
          projectId={projectID}
          parentType="Project"
          handleAddRow={handleAddRow}
          handleCancelAddRow={handleCancelAddRow}
          setData={setData}
          triggerSearch={handleSearchTrigger}
        />
      </div>
    </ColumnConfigProvider>
  );
};

export default InvoiceRegistry;
